import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Business as BusinessIcon, Description as DocIcon } from '@mui/icons-material';
import { supabase } from '../services/supabaseClient';
import { Document } from '../types/documents';

interface QuickReplayNavigationProps {
  onReplaySelect: (documentId: number | string) => void;
  selectedDocId?: number | string | null;
  language?: 'English' | 'Hebrew';
}

export default function QuickReplayNavigation({ 
  onReplaySelect, 
  selectedDocId, 
  language = 'English' 
}: QuickReplayNavigationProps) {
  const [companySpecificReplays, setCompanySpecificReplays] = useState<Document[]>([]);
  const [generalReplays, setGeneralReplays] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fontFamily = language === 'Hebrew' 
    ? 'Segoe UI, Tahoma, Geneva, Verdana, Arial, sans-serif, "Segoe UI Hebrew", "Noto Sans Hebrew"'
    : 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';

  useEffect(() => {
    fetchRecentReplays();
  }, []);

  const fetchRecentReplays = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch company-specific audio/video files (has company_id)
      // Use inner join with chunks to ensure we only list processed files
      const { data: companyReplays, error: companyError } = await supabase
        .from('documents')
        .select('*, chunks!inner(id)')
        .in('doc_type', ['Audio', 'audio', 'video', 'Video', 'media'])
        .not('company_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(5);

      if (companyError) throw companyError;

      // Fetch general audio/video files (no company_id)
      const { data: generalReplaysData, error: generalError } = await supabase
        .from('documents')
        .select('*, chunks!inner(id)')
        .in('doc_type', ['Audio', 'audio', 'video', 'Video', 'media'])
        .is('company_id', null)
        .order('created_at', { ascending: false })
        .limit(5);

      if (generalError) throw generalError;



      setCompanySpecificReplays(companyReplays || []);
      setGeneralReplays(generalReplaysData || []);
    } catch (err: any) {
      console.error('Error fetching recent replays:', err);
      setError(err.message || 'Failed to fetch recent replays');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'No date';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format the date manually to avoid timezone conversion
    const year = date.getUTCFullYear();
    const month = date.toLocaleString(language === 'Hebrew' ? 'he-IL' : 'en-US', { month: 'short' });
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${month} ${day}, ${year}, ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getReplayTitle = (doc: Document) => {
    if (doc.title) return doc.title;
    
    // Fallback title based on available data
    const parts = [];
    if (doc.company_name) parts.push(doc.company_name);
    if (doc.analyst_name) parts.push(doc.analyst_name);
    if (doc.sector_name) parts.push(doc.sector_name);
    
    return parts.length > 0 ? parts.join(' • ') : `Audio ${doc.id}`;
  };

  const renderReplayList = (replays: Document[], title: string, icon: React.ReactNode) => (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ fontFamily, color: '#1A2341', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      
      {replays.length === 0 ? (
        <Typography sx={{ fontFamily, color: 'text.secondary', fontStyle: 'italic' }}>
          {language === 'Hebrew' ? 'אין קבצי אודיו זמינים' : 'No audio files available'}
        </Typography>
      ) : (
        <List sx={{ p: 0 }}>
          {replays.map((replay, index) => (
            <React.Fragment key={replay.id}>
              <ListItem sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => onReplaySelect(replay.id)}
                  selected={selectedDocId === replay.id}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: '#E3F2FD',
                      '&:hover': {
                        bgcolor: '#BBDEFB',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ 
                        fontFamily, 
                        fontWeight: selectedDocId === replay.id ? 600 : 400,
                        fontSize: '0.9rem',
                        direction: language === 'Hebrew' ? 'rtl' : 'ltr',
                        textAlign: language === 'Hebrew' ? 'right' : 'left',
                        unicodeBidi: 'plaintext'
                      }}>
                        {getReplayTitle(replay)}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily, color: 'text.secondary' }}>
                          {(replay as any).business_time ? formatDate((replay as any).business_time) : 
                           replay.created_at ? formatDate(replay.created_at) : 'No date'}
                        </Typography>
                        {replay.chunk_count && (
                          <Chip 
                            label={`${replay.chunk_count} ${language === 'Hebrew' ? 'חלקים' : 'chunks'}`} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontFamily, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip 
                          label={language === 'Hebrew' ? 'אודיו' : 'Audio'} 
                          size="small" 
                          color="primary"
                          sx={{ fontFamily, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              {index < replays.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography sx={{ fontFamily }}>
            {language === 'Hebrew' ? 'טוען קבצי אודיו אחרונים...' : 'Loading recent audio files...'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2, bgcolor: '#fff' }}>
        <Alert severity="error" sx={{ fontFamily }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 2, bgcolor: '#fff', height: 'fit-content' }}>
      <Typography variant="h6" sx={{ 
        fontFamily, 
        color: '#1A2341', 
        fontWeight: 700, 
        mb: 2,
        direction: language === 'Hebrew' ? 'rtl' : 'ltr',
        textAlign: language === 'Hebrew' ? 'right' : 'left',
        unicodeBidi: 'plaintext',
        fontSize: '1rem'
      }}>
        {language === 'Hebrew' ? 'קבצי אודיו אחרונים' : 'Recent Audio Files'}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          {renderReplayList(
            generalReplays, 
            language === 'Hebrew' ? 'כללי' : 'General',
            <DocIcon sx={{ color: '#4A90E2', fontSize: '1.2rem' }} />
          )}
        </Box>
        
        <Box>
          {renderReplayList(
            companySpecificReplays, 
            language === 'Hebrew' ? 'חברות' : 'Company',
            <BusinessIcon sx={{ color: '#2B3A67', fontSize: '1.2rem' }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
} 